import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { usersService } from "../services/usersService";

// Re-export types from user.ts
export type { UserProfile, UserRole, TeamType, UserPersona } from "@/types/user";

// UserContext type definition
type UserContextType = {
  user: any | null;
  loading: boolean;
  isAuthenticated: boolean;
  updateUserProfile: (updates: any) => Promise<void>;
  refreshUser: () => Promise<void>;
  userPersona: string | null;
  settings: any | null;
  preferences: any | null;
  updateSettings: (settings: any) => Promise<void>;
  updatePreferences: (preferences: any) => Promise<void>;
};

// Create context with default values
const UserContext = createContext<UserContextType>({
  user: null,
  loading: true,
  isAuthenticated: false,
  updateUserProfile: async () => {},
  refreshUser: async () => {},
  userPersona: null,
  settings: null,
  preferences: null,
  updateSettings: async () => {},
  updatePreferences: async () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const { user: authUser, isAuthenticated, loading: authLoading } = useAuth();
  const [user, setUser] = useState<any | null>(null);
  const [settings, setSettings] = useState<any | null>(null);
  const [preferences, setPreferences] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Fetch user profile, settings, and preferences
   */
  const fetchUserData = useCallback(async () => {
    // Check if we have tokens in localStorage - if not, don't fetch
    const hasAccessToken = localStorage.getItem('access_token');
    const hasRefreshToken = localStorage.getItem('refresh_token');
    
    // Don't fetch if not authenticated, no profile ID, or no tokens
    if (!isAuthenticated || !authUser?.profile?.id || !hasAccessToken || !hasRefreshToken) {
      setUser(null);
      setSettings(null);
      setPreferences(null);
      setLoading(false);
      return;
    }

    try {
      const userId = authUser.profile.id;

      // Fetch profile, settings, and preferences in parallel
      const [profileData, settingsData, preferencesData] = await Promise.all([
        usersService.getUserById(userId),
        usersService.getSettings(userId).catch(() => null),
        usersService.getPreferences(userId).catch(() => null),
      ]);

      setUser(profileData);
      setSettings(settingsData);
      setPreferences(preferencesData);
    } catch (error) {
      console.error("Error fetching user data:", error);
      // On error, clear user state but DON'T clear tokens
      // Token refresh should handle expired tokens via apiService interceptor
      setUser(null);
      setSettings(null);
      setPreferences(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authUser?.profile?.id]);

  /**
   * Load user data when authenticated
   */
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    // Check tokens first - if no tokens, definitely not authenticated
    const hasAccessToken = localStorage.getItem('access_token');
    const hasRefreshToken = localStorage.getItem('refresh_token');
    
    // Only fetch if authenticated AND has valid authUser with profile AND has tokens
    if (isAuthenticated && authUser?.profile?.id && hasAccessToken && hasRefreshToken) {
      fetchUserData();
    } else {
      // Clear user data when not authenticated or no tokens
      setUser(null);
      setSettings(null);
      setPreferences(null);
      setLoading(false);
    }
  }, [isAuthenticated, authUser, authLoading, fetchUserData]);

  /**
   * Update user profile
   */
  const updateUserProfile = useCallback(
    async (updates: any) => {
      if (!user?.id) return;

      try {
        const updated = await usersService.updateProfile(user.id, updates);
        setUser((prev: any) => ({ ...prev, ...updated }));
      } catch (error) {
        console.error("Error updating profile:", error);
        throw error;
      }
    },
    [user?.id]
  );

  /**
   * Update settings
   */
  const updateSettings = useCallback(
    async (newSettings: any) => {
      if (!user?.id) return;

      try {
        const updated = await usersService.updateSettings(user.id, newSettings);
        setSettings(updated);
      } catch (error) {
        console.error("Error updating settings:", error);
        throw error;
      }
    },
    [user?.id]
  );

  /**
   * Update preferences
   */
  const updatePreferences = useCallback(
    async (newPreferences: any) => {
      if (!user?.id) return;

      try {
        const updated = await usersService.updatePreferences(user.id, newPreferences);
        setPreferences(updated);
      } catch (error) {
        console.error("Error updating preferences:", error);
        throw error;
      }
    },
    [user?.id]
  );

  /**
   * Refresh user data
   */
  const refreshUser = useCallback(async () => {
    await fetchUserData();
  }, [fetchUserData]);

  const value: UserContextType = {
    user,
    loading: authLoading || loading,
    isAuthenticated: isAuthenticated && !!user,
    updateUserProfile,
    refreshUser,
    userPersona: user?.persona || null,
    settings,
    preferences,
    updateSettings,
    updatePreferences,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};
